import { useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { Patient, Encounter, Condition } from '@medplum/fhirtypes';
import {
  ConditionInputItem,
  prepareConditionForSave,
} from '@/utils/condition.utils';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

export interface UseConditionActionsReturn {
  saveCondition: (
    conditionInput: ConditionInputItem,
    isMedicalHistory?: boolean
  ) => Promise<ConditionInputItem>;

  deleteCondition: (conditionInput: ConditionInputItem) => Promise<void>;
}

export function useConditionActions(
  patient: Patient,
  encounter: Encounter | undefined
): UseConditionActionsReturn {
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages']);

  const saveCondition = useCallback(
    async (
      conditionInput: ConditionInputItem,
      isMedicalHistory: boolean = false
    ): Promise<ConditionInputItem> => {
      if (!encounter) {
        notifications.show({
          color: 'red',
          title: t('examination.conditions.encounterRequired', {
            defaultValue: 'Encounter is required to save a condition',
          }),
          message: t('examination.conditions.encounterRequiredMessage', {
            defaultValue: 'Please create an encounter first',
          }),
        });
        return Promise.reject(
          new Error('Encounter is required to save a condition')
        );
      }

      // Prepare Condition resources
      const {
        encounterDiagnosis,
        problemListItem,
        shouldDeleteProblemListItem,
      } = prepareConditionForSave(
        patient,
        encounter,
        conditionInput,
        isMedicalHistory
      );

      // Save or update encounter diagnosis
      const savedEncounterDiagnosis = conditionInput.encounterDiagnosisId
        ? await medplum.updateResource(encounterDiagnosis)
        : await medplum.createResource(encounterDiagnosis);

      // Handle problem list item
      let savedProblemListItem: Condition | undefined;
      if (shouldDeleteProblemListItem && conditionInput.problemListItemId) {
        // Delete existing problem list item
        await medplum.deleteResource(
          'Condition',
          conditionInput.problemListItemId
        );
      } else if (problemListItem) {
        // Create or update problem list item
        savedProblemListItem = conditionInput.problemListItemId
          ? await medplum.updateResource(problemListItem)
          : await medplum.createResource(problemListItem);
      }

      // Update conditionInput with server-assigned IDs
      const updatedConditionInput: ConditionInputItem = {
        ...conditionInput,
        encounterDiagnosisId: savedEncounterDiagnosis.id,
        problemListItemId: savedProblemListItem?.id,
      };

      // Invalidate cache so subsequent reads get fresh data
      medplum.invalidateSearches('Condition');

      return updatedConditionInput;
    },
    [medplum, patient, encounter, t]
  );

  const deleteCondition = useCallback(
    async (conditionInput: ConditionInputItem): Promise<void> => {
      // Delete encounter diagnosis if it exists
      if (conditionInput.encounterDiagnosisId) {
        await medplum.deleteResource(
          'Condition',
          conditionInput.encounterDiagnosisId
        );
      }

      // Delete problem list item if it exists
      if (conditionInput.problemListItemId) {
        await medplum.deleteResource(
          'Condition',
          conditionInput.problemListItemId
        );
      }

      // Invalidate cache
      medplum.invalidateSearches('Condition');
    },
    [medplum]
  );

  return { saveCondition, deleteCondition };
}
